import axios from 'axios'
import { CatalogItem } from '../../domain/entities/Catalogue'
import {
  CatalogueRepository,
  GetCatalogFilters,
  GetCatalogResponse,
} from '../../domain/repositories/CatalogueRepository'
import { CatalogItemApiResponse, catalogueApiDatasource } from '../datasources/CatalogueApiDatasource'

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined
    return data?.message || data?.error || fallback
  }

  return err instanceof Error ? err.message : fallback
}

const toDomainItem = (i: CatalogItemApiResponse): CatalogItem => ({
  id: i.id,
  name: i.name,
  variant: i.variant,
  description: i.description,
  images: i.images || [],
  unit: i.unit,
  packWeightKg: i.pack_weight_kg,
  moq: i.moq,
  stock: i.stock,
  mrp: i.mrp,
  sellingPrice: i.selling_price,
  manufacturerPrice: i.manufacturer_price || 0,
  addedBy: i.added_by,
  isDeleted: i.is_deleted,
  createdAt: i.created_at,
  updatedAt: i.updated_at,
})

export class CatalogueRepositoryImpl implements CatalogueRepository {
  public async getCatalogItems(filters: GetCatalogFilters): Promise<GetCatalogResponse> {
    try {
      const res = await catalogueApiDatasource.getCatalogItems({
        skip: (filters.page - 1) * filters.limit,
        limit: filters.limit,
        search: filters.search || undefined,
        status: filters.status && filters.status !== 'all' ? filters.status : undefined,
      })

      return {
        items: res.data.map(toDomainItem),
        totalItems: res.total,
        totalPages: Math.max(1, Math.ceil(res.total / filters.limit)),
      }
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to load catalogue items.'))
    }
  }

  public async getCatalogItemById(id: string): Promise<CatalogItem | null> {
    try {
      const item = await catalogueApiDatasource.getCatalogItemById(id)
      return toDomainItem(item)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw new Error(extractErrorMessage(err, 'Failed to load catalogue item.'))
    }
  }

  public async createCatalogItem(
    item: Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>,
  ): Promise<CatalogItem> {
    try {
      const created = await catalogueApiDatasource.createCatalogItem({
        name: item.name,
        variant: item.variant,
        description: item.description,
        images: item.images,
        unit: item.unit,
        pack_weight_kg: item.packWeightKg,
        moq: item.moq,
        mrp: item.mrp,
        selling_price: item.sellingPrice,
        manufacturer_price: item.manufacturerPrice,
        stock: item.stock,
      })
      return toDomainItem(created)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to create catalogue item.'))
    }
  }

  public async updateCatalogItem(
    id: string,
    item: Partial<Omit<CatalogItem, 'id' | 'addedBy' | 'isDeleted' | 'createdAt' | 'updatedAt'>>,
  ): Promise<CatalogItem> {
    try {
      const updated = await catalogueApiDatasource.updateCatalogItem(id, {
        name: item.name,
        variant: item.variant,
        description: item.description,
        images: item.images,
        unit: item.unit,
        pack_weight_kg: item.packWeightKg,
        clear_pack_weight_kg: 'packWeightKg' in item && item.packWeightKg === undefined,
        moq: item.moq,
        mrp: item.mrp,
        selling_price: item.sellingPrice,
        manufacturer_price: item.manufacturerPrice,
        stock: item.stock,
      })
      return toDomainItem(updated)
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to update catalogue item.'))
    }
  }

  public async deleteCatalogItem(id: string): Promise<void> {
    try {
      await catalogueApiDatasource.updateCatalogItem(id, { is_deleted: true })
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to delete catalogue item.'))
    }
  }

  public async restoreCatalogItem(id: string): Promise<void> {
    try {
      await catalogueApiDatasource.updateCatalogItem(id, { is_deleted: false })
    } catch (err) {
      throw new Error(extractErrorMessage(err, 'Failed to restore catalogue item.'))
    }
  }
}

export const catalogueRepository = new CatalogueRepositoryImpl()
